import {
  Component,
  DestroyRef,
  Injector,
  NgZone,
  OnChanges,
  OnInit,
  SimpleChanges,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import {
  EMPTY,
  forkJoin,
  merge,
  Observable,
  of,
  Subscription,
  throwError,
  timer,
} from "rxjs";
import { catchError, finalize, retry, tap } from "rxjs/operators";

import { addWeeks, format } from "date-fns";
import { currencies } from "../../shared/currencies/currencies.const";
import { FreightRate } from "./models/freight-rate.model";
import { freightRateGroupSeparator } from "./freight-rate-group/freight-rate-group-separator.const";
import { FreightRateGroup } from "./freight-rate-group/freight-rate-group.model";
import { FreightRateGroupService } from "./freight-rate-group/freight-rate-group.service";
import { RateMapper } from "../../rate/rate.mapper";
import { LclQuote } from "../models/quote-lcl.model";
import { FclQuote } from "../models/quote-fcl.model";
import { RouteMapper } from "../../route/route.mapper";
import { FclRoute } from "../../route/route-fcl.model";
import { User } from "../../user/user.model";
import { HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import { role } from "../../user/role.const";
import { organizationTab } from "../../organization/organization-tab.const";
import { NotifyService } from "../../notify/notify.service";
import { RateRepository } from "../../rate/rate.repository";
import { TOAST } from "../../notify/toast/toast-type.enum";
import { eventType } from "../../notify/event.const";
import { Route } from "../../route/route-type";
import { rateType } from "../../rate/rate-type.const";
import { rateStatus } from "../../rate/rate-status.const";
import { RouteRepository } from "../../route/route.repository";
import { RouteBatchItem } from "../../route/route-batch.model";
import { UserService } from "../../user/user.service";
import { noop } from "../../shared/noop.function";
import { quoteType } from "../quote-form/quote-type.const";
import { LclRate } from "../../rate/rate-lcl.model";
import { FclRate } from "../../rate/rate-fcl.model";
import { ContainerName } from "../../shared/container/container-name.type.const";
import { FclTier } from "../../tier/tier-fcl.model";
import { BaseStandardQuote } from "../models/quote-base-standard.model";
import { AirQuote } from "../models/quote-air.model";
import { PartnerRateCriteria } from "./models/partner-rate-criteria.model";
import { PartnerRateSelectionService } from "./partner-rate-selection.service";
import { OnlineRateCacheService } from "./online-rate-cache.service";
import { OnlineCarrierRenewingError } from "../../rate/online-carrier-renewing.error";
import { InViewportDirective } from "../../shared/in-viewport/in-viewport.directive";
import { AirRate } from "../../rate/rate-air.model";
import { EventService } from "../../shared/event/event.service";
import { SortEquipmentPipe } from "../../shared/container/sort-equipment.pipe";
import { FormatContainerLabelPipe } from "../../shared/container/format-container-label.pipe";
import { containerNames } from "../../shared/container/container.const";
import { LclRoute } from "../../route/route-lcl.model";
import { AirRoute } from "../../route/route-air.model";
import { BaseLinkedRate } from "../../rate/rate-base-linked.model";
import { tableColumn } from "../../ui/table/table-column.const";
import { ITableColumn } from "../../ui/table/table-column.interface";
import { LtlQuote } from "../models/quote-ltl.model";
import { LtlRate } from "../../rate/rate-ltl.model";
import { LtlRoute } from "../../route/route-ltl.model";
import { LtlPickupDeliveryRate } from "../../rate/rate-ltl-pickup-delivery.model";
import { Rate } from "../../rate/rate-type";
import { LtlLinkedRate } from "../../rate/rate-ltl-linked.model";
import { rateSource } from "../../rate/rate-source.const";
import { AirTariffQuote } from "../models/quote-airtariff.model";
import { airWeightBrackets } from "app/shared/form/fields/air-weight-brackets.const";
import { getWeightBracketsHeader } from "app/shared/weight-brackets/get-weight-bracket-header.function";
import { AirWeightBracketName } from "app/shared/air-weight-bracket/air-weight-bracket-name.type.const";
import { rateDiscriminator } from "app/rate/rate-discriminator.const";
import { FtlFlatRate } from "app/rate/rate-ftl-flat.model";
import { FtlRoute } from "app/route/route-ftl.model";
import { PtlFlatRate } from "app/rate/rate-ptl-flat.model";
import { PtlRoute } from "app/route/route-ptl.model";
import { onlineCarrierType } from "app/rate/online-carrier-type.const";
import {
  OnlineCarrier,
  OnlineCarrierAction,
} from "app/rate/online-carrier.interface";
import { BackendErrorPipe } from "app/shared/backend/backend-error.pipe";
import { FreightRateBatchSelectComponent } from "./freight-rate-batch-select/freight-rate-batch-select.component";
import { FreightRateBatchMarginComponent } from "./freight-rate-batch-margin/freight-rate-batch-margin.component";
import { TableComponent } from "../../ui/table/table.component";
import { QuoteMaerskLocationsComponent } from "../quote-maersk-locations/quote-maersk-locations.component";
import { quoteStatus } from "../quote-form/quote-status.const";
import { AgentRequestRepository } from "../agent-request.repository";
import {
  AgentRequestCounts,
  agentRequestGroup,
  AgentRequestTarget,
  agentRequestType,
} from "../models/agent-request.model";
import { agentRequestListParams } from "../agent-request-list/agent-request-list-params.const";
import { AgentRequestLinksComponent } from "../agent-request-links/agent-request-links.component";
import { AgentRequestDialogComponent } from "../agent-request-dialog/agent-request-dialog.component";

// Quote types whose Freight tab lanes can be sent to an agent for rates.
const agentRequestQuoteTypes: readonly string[] = [
  quoteType.fcl,
  quoteType.lcl,
  quoteType.air,
  quoteType.ltl,
  quoteType.ftl,
  quoteType.ptl,
];

// Module scope on purpose: isRowDisabled runs for every cell of the row being
// edited on every change-detection pass, so rebuilding these per call allocated
// three arrays per cell per keystroke.
const disabledForEveryoneColumns: readonly string[] = [
  "chargeableWeight",
  "noMarginPrice",
];
const disabledForLtlColumns: readonly string[] = [];
// The backend answers a tick with 409 when the rate it names can no longer be offered.
const rateNoLongerAvailableStatus = 409;
// The backend refuses a paid carrier search with 409 once the organization's credits are spent.
const noSearchCreditsStatus = 409;
const searchAllowanceReachedMessage =
  "<b>You've reached your monthly search allowance.</b><br>";
// A background carrier reconnect is asked after for about five minutes.
const RENEWING_POLL_MS = 15_000;
const RENEWING_POLLS = 20;
const disabledForSalesColumns: readonly string[] = [
  ...containerNames,
  "name",
  "carrier",
  "validity",
  "currency",
  "minPrice",
  "chargeableWeightPrice",
  "blPrice",
  "internalNote",
];

// Tally of one unattended partner-rate pass, shared by the lanes it fetches so
// the last one to settle can report a single outcome for all of them.
interface PartnerRatePass {
  pending: number;
  failed: number;
}

// One partner request in flight, with the pass it reports to - a lane carried
// over into a later refresh still settles the pass that asked for it.
interface PartnerRateRequest {
  subscription: Subscription;
  pass: PartnerRatePass;
}

@Component({
  selector: "app-quote-freight-rate",
  templateUrl: "./quote-freight-rate.component.html",
  styleUrls: ["./quote-freight-rate.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FreightRateBatchSelectComponent,
    FreightRateBatchMarginComponent,
    TableComponent,
    QuoteMaerskLocationsComponent,
    InViewportDirective,
    AgentRequestLinksComponent,
    AgentRequestDialogComponent,
  ],
})
export class QuoteFreightRateComponent implements OnInit, OnChanges {
  private notifyService = inject(NotifyService);
  private eventService = inject(EventService);
  private rateRepository = inject(RateRepository);
  private routeRepository = inject(RouteRepository);
  private freightRateGroupService = inject(FreightRateGroupService);
  private userService = inject(UserService);
  private partnerRateSelection = inject(PartnerRateSelectionService);
  private onlineRateCache = inject(OnlineRateCacheService);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);
  private ngZone = inject(NgZone);
  private agentRequestRepository = inject(AgentRequestRepository);
  private router = inject(Router);

  public readonly quote = input<BaseStandardQuote>(undefined);
  public readonly equipment = input<ContainerName[]>([]);
  public readonly locationKindFilter = input("");
  public readonly locationKindFilterOptions = input([]);
  public readonly batchActionsAvailable = input(true);
  // Off for a multi quote's children, which are plain FCL/LCL/AIR quotes themselves.
  public readonly agentRequestsVisible = input(true);
  // True while the form is saving the quote. The rate list is validated
  // against the saved quote, so a fetch before the save lands is rejected.
  public readonly quoteLoading = input(false);
  public readonly routeDetect = output<{
    detected: boolean;
    quote: BaseStandardQuote;
    count: number;
    rates: Rate[];
  }>();

  public groupSep = freightRateGroupSeparator;
  public quoteType = quoteType;
  public freightRateGroups: Record<string, FreightRateGroup> = {};
  public freightRateGroupNames: string[] = [];
  public user: User;
  public loading = false;
  public refreshingRows = false;
  // A batch request in flight locks its control until the routes are saved.
  public batchSelectionSaving = false;
  public batchMarginSaving = false;
  private readonly batchSelect = viewChild(FreightRateBatchSelectComponent);
  public selectedTier: FclTier;
  public columns: ITableColumn[] = [];
  public addable = true;
  public exportable = false;

  public maerskLocationsVisible = false;
  public currentFreightRateGroup: FreightRateGroup = null;

  // What the backend lists for the quote on screen; every lane gets its own
  // copy to track its load state on.
  private onlineCarriers: OnlineCarrier[] = [];
  private onlineCarrierSubscription: Subscription;
  // The organization's balance, asked for only when a listed carrier is paid.
  public searchCredits: number | null = null;
  private searchCreditSubscription: Subscription;
  private searchAllowanceMessageOpen = false;
  // Okargo keeps its own button; on LCL and AIR "other" is the only source.
  public readonly searchAllSkips = computed(() =>
    this.quote() instanceof FclQuote ? [onlineCarrierType.other] : [],
  );
  public readonly showsAgentRequests = computed(
    () =>
      this.agentRequestsVisible() &&
      agentRequestQuoteTypes.includes(this.quote()?.getQuoteType()),
  );
  public readonly laneAgentRequestParams = computed(() => ({
    [agentRequestListParams.quote]: this.quote()?.getId(),
    [agentRequestListParams.type]: agentRequestGroup.rates,
  }));
  public agentRequestCounts = new AgentRequestCounts();
  private agentRequestCountSubscription: Subscription;
  // The lane the request popup is open for; null while it is closed.
  public agentRequestTarget: AgentRequestTarget | null = null;

  private rateListSubscription: Subscription;
  // Partner requests in flight, keyed by partnerLaneKey(). A refresh leaves a
  // lane still on screen to its request - the partner takes seconds to answer
  // and a batch selection refreshes once per created route - and cancels only
  // the lanes that left the screen.
  private partnerRateRequests = new Map<string, PartnerRateRequest>();
  // Partner rows already fetched, keyed by partnerLaneKey() - everything the
  // partner request is built from. Every refresh rebuilds the groups and drops
  // the rows the previous pass appended, so a lane already in here is served
  // from the cache instead of requested again; a changed leg or criterion
  // yields a new key and is fetched. Lives as long as the component - a page
  // reload fetches afresh.
  private partnerRatesByLane = new Map<string, FreightRate[]>();
  // Lanes scrolled into view so far, by group name. Only these are asked for
  // partner rates - a quote with twenty lanes would otherwise ask the partner
  // twenty times on open, for rows mostly never looked at.
  private seenLanes = new Set<string>();
  private partnerRatesScheduled = false;
  // Set while the first fetch waits for the form's save to land.
  private fetchDeferred = false;

  ngOnInit() {
    this.updateColumns();
    this.updateExportable();
    this.fetchOnlineCarriers();
    this.fetchAgentRequestCounts();
    if (this.quoteLoading()) {
      // Mounted by the very change that is still being saved (the field that
      // made the quote valid). The form re-fetches once the save lands; show
      // the lanes with the skeleton until then instead of a request the
      // backend would reject.
      this.fetchDeferred = true;
      this.showFreightRateSkeleton();
    } else {
      this.fetchFreightRates();
    }
    this.userService.getUser().then((user) => (this.user = user), noop);
    this.updateNewFreightRates(this.freightRateGroups);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.quote) {
      this.updateExportable();

      const previous: BaseStandardQuote = changes.quote.previousValue;
      const current: BaseStandardQuote = changes.quote.currentValue;
      if (
        !!previous &&
        !!current &&
        (previous.getId() !== current.getId() ||
          previous.getQuoteType() !== current.getQuoteType())
      ) {
        // Another quote is on screen; its lanes must not be served rows the
        // previous one fetched.
        this.partnerRateRequests.forEach((request) =>
          request.subscription.unsubscribe(),
        );
        this.partnerRateRequests.clear();
        this.partnerRatesByLane.clear();
        this.fetchOnlineCarriers();
        this.fetchAgentRequestCounts();
        this.fetchFreightRates();
        this.updateColumns();
        this.updateNewFreightRates(this.freightRateGroups);
      }
    }

    if (changes.quoteLoading && !changes.quoteLoading.currentValue) {
      this.fetchDeferredFreightRates();
    }
  }

  // Not every save re-fetches the rates (a failed one, a comment), so the
  // deferred fetch cannot rely on the form. Wait for the render the form's own
  // re-fetch is queued on: when that one ran, it cleared the flag. Render hooks
  // run outside NgZone, hence the re-entry.
  private fetchDeferredFreightRates(): void {
    if (!this.fetchDeferred) {
      return;
    }

    afterNextRender(
      () => {
        if (this.fetchDeferred) {
          this.ngZone.run(() => this.fetchFreightRates());
        }
      },
      { injector: this.injector },
    );
  }

  public requestRatesFromAgent(group: FreightRateGroup): void {
    this.agentRequestTarget = {
      type: agentRequestType.freightRate,
      originLegId: group.quoteOriginLocationId ?? null,
      destinationLegId: group.quoteDestinationLocationId ?? null,
      originName: group.originLabel ?? null,
      destinationName: group.destinationLabel ?? null,
      inlandHaulage: null,
    };
  }

  public onAgentRequestsSent(): void {
    this.agentRequestTarget = null;
    this.fetchAgentRequestCounts();
  }

  private fetchAgentRequestCounts(): void {
    this.agentRequestCountSubscription?.unsubscribe();
    this.agentRequestCounts = new AgentRequestCounts();

    const quote = this.quote();
    if (!this.showsAgentRequests() || !quote?.getId()) {
      return;
    }

    this.agentRequestCountSubscription = this.agentRequestRepository
      .getQuoteAgentRequestCounts(quote.getId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (counts) => (this.agentRequestCounts = counts),
        error: noop,
      });
  }

  private fetchOnlineCarriers(): void {
    this.onlineCarrierSubscription?.unsubscribe();
    this.setOnlineCarriers([]);

    const quote = this.quote();
    if (!quote?.getId()) {
      return;
    }

    this.onlineCarrierSubscription = this.rateRepository
      .getOnlineCarriers(quote.getId())
      .subscribe({
        next: (carriers) => this.setOnlineCarriers(carriers),
        error: () =>
          this.notifyService.toast(
            TOAST.ERROR,
            "Online carriers were not fetched",
          ),
      });
  }

  private setOnlineCarriers(carriers: OnlineCarrier[]): void {
    this.onlineCarriers = carriers;
    this.seedOnlineCarriers(this.freightRateGroups);
    this.fetchSearchCredits();
  }

  private fetchSearchCredits(): void {
    this.searchCreditSubscription?.unsubscribe();

    const quoteId = this.quote()?.getId();
    if (!quoteId || !this.onlineCarriers.some((carrier) => carrier.paid)) {
      this.searchCredits = null;
      return;
    }

    this.searchCreditSubscription = this.rateRepository
      .getSearchCredits(quoteId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (credits) => (this.searchCredits = credits),
        error: noop,
      });
  }

  private isPaidCarrier(type: string): boolean {
    return this.onlineCarriers.some(
      (carrier) => carrier.type === type && carrier.paid,
    );
  }

  // A carrier the lane loaded within the hour keeps its button gone: its rows
  // are served from the cache.
  private seedOnlineCarriers(groups: Record<string, FreightRateGroup>): void {
    Object.values(groups).forEach((group) => {
      if (!group.canLoadOnlineRates) {
        group.onlineCarriers = [];
        return;
      }

      const loaded = this.cachedOnlineCarrierTypes(group);
      group.onlineCarriers = this.onlineCarriers
        .filter((carrier) => !loaded.includes(carrier.type))
        .map((carrier) => ({ ...carrier }));
    });
  }

  private cachedOnlineCarrierTypes(group: FreightRateGroup): string[] {
    const quoteId = this.quote()?.getId();
    if (!quoteId || this.onlineCarriers.length === 0) {
      return [];
    }

    return this.onlineRateCache.loadedCarrierTypes(
      quoteId,
      this.partnerLaneKey(group),
    );
  }

  private patchOnlineCarrier(
    group: FreightRateGroup,
    type: string,
    patch: Partial<OnlineCarrierAction>,
  ): void {
    // A new array, not a mutated one: the table reads it through a signal input.
    group.onlineCarriers = group.onlineCarriers.map((carrier) =>
      carrier.type === type ? { ...carrier, ...patch } : carrier,
    );
  }

  // A loaded carrier leaves its lane; seedOnlineCarriers() keeps it out of the
  // rebuilt groups for as long as the cache serves its rows.
  private removeOnlineCarrier(group: FreightRateGroup, type: string): void {
    group.onlineCarriers = group.onlineCarriers.filter(
      (carrier) => carrier.type !== type,
    );
  }

  public onRowSelect(freightRate: FreightRate): void {
    if (
      freightRate.rate.getSource() === rateSource.online &&
      (isNaN(freightRate.rate.getId()) || !freightRate.rate.getId())
    ) {
      this.createRate(freightRate);
    } else {
      this.createRoute(freightRate);
    }
  }

  public onRowUnselect(freightRate: FreightRate): void {
    this.deleteRoute(freightRate);
  }

  public onSaveFreightRate(freightRate: FreightRate): void {
    const rate = freightRate.getRate();
    const route = freightRate.getRoute();

    if (rate instanceof PtlFlatRate) {
      const chargeableWeightPrice =
        route instanceof PtlRoute && route.hasChargeableWeightPrice()
          ? route.getChargeableWeightPrice()
          : rate.getChargeableWeightPrice();
      rate.setChargeableWeightPrice(chargeableWeightPrice);
      if (route instanceof PtlRoute) {
        route.setBlPrice(rate.getBlPrice());
      }
    }

    if (!rate.getId()) {
      this.createRate(freightRate);
      return;
    }

    if (!this.canEditRate(this.user, freightRate)) {
      if (route) {
        this.updateRoute(route);
      }
      return;
    }

    if (rate instanceof LtlRate && rate.getType() == rateType.spot) {
      this.rateRepository.updateLtlSpotRatePrice(rate, this.quote()).subscribe({
        next: () => {
          this.updateRate(rate);
          if (route) {
            this.updateRoute(route);
          }
        },
        error: () => {
          this.notifyService.toast(
            TOAST.ERROR,
            "An unexpected error while updating the rate.",
          );
        },
      });
      return;
    }

    if (rate instanceof FtlFlatRate && rate.getType() == rateType.spot) {
      this.rateRepository.updateFtlSpotRatePrice(rate, this.quote()).subscribe({
        next: () => {
          this.updateRate(rate);
          if (route) {
            this.updateRoute(route);
          }
        },
        error: () => {
          this.notifyService.toast(
            TOAST.ERROR,
            "An unexpected error while updating the rate.",
          );
        },
      });
      return;
    }

    if (rate instanceof PtlFlatRate && rate.getType() == rateType.spot) {
      this.rateRepository.updatePtlSpotRatePrice(rate, this.quote()).subscribe({
        next: () => {
          if (route) {
            this.updateRoute(route);
          }
        },
        error: () => {
          this.notifyService.toast(
            TOAST.ERROR,
            "An unexpected error while updating the rate.",
          );
        },
      });
      return;
    }

    this.updateRate(rate);
    if (route) {
      this.updateRoute(route);
    }
  }

  public selectMaerskLocations(freightRateGroup: FreightRateGroup): void {
    this.currentFreightRateGroup = freightRateGroup;
    if (
      (this.quote().getOriginLocations().length &&
        this.quote().getOriginLocations()[0].country) ||
      (this.quote().getDestinationLocations().length &&
        this.quote().getDestinationLocations()[0].country)
    ) {
      this.maerskLocationsVisible = true;
    } else {
      this.onLoadMaerskRows(freightRateGroup);
    }
  }

  // Arrow property: the table calls this unbound, so a method would lose `this`.
  public isRowDisabled = (
    columnName: string,
    freightRate: FreightRate,
  ): boolean => {
    const canEdit =
      freightRate.getRate().getSource() !== rateSource.online &&
      freightRate.getRate().getType() === rateType.spot &&
      freightRate.getRate().getStatus() === rateStatus.draft;

    // Only the non-draft case needs a copy; every other pass reuses the shared array.
    const disabledForEveryone =
      this.quote().getStatus() !== quoteStatus.draft
        ? [...disabledForEveryoneColumns, "comment"]
        : disabledForEveryoneColumns;
    const disabledForLtl = disabledForLtlColumns;
    const disabledForSales = disabledForSalesColumns;

    if (
      freightRate.getRate().getDiscriminator() == rateDiscriminator.ltl &&
      freightRate.getRate().getType() == rateType.spot &&
      freightRate.getRate().getId() &&
      disabledForLtl.indexOf(columnName) >= 0
    ) {
      return true;
    }

    if (
      freightRate.getRate() instanceof PtlFlatRate &&
      freightRate.getRate().getType() === rateType.spot &&
      columnName === "carrier"
    ) {
      return true;
    }

    return (
      disabledForEveryone.indexOf(columnName) >= 0 ||
      (!canEdit && disabledForSales.indexOf(columnName) >= 0)
    );
  };

  // A route's table renders only once it scrolls into view. Until then its
  // placeholder reserves roughly the table's height, so the page does not jump
  // while the tables above render. Measured on the desktop layout: column
  // header and footer take 127px, each row 46px; an empty table is 210px.
  public tablePlaceholderHeight(group: FreightRateGroup): number {
    const rows = this.loading
      ? TableComponent.SKELETON_ROW_COUNT
      : group.freightRates.length;

    return rows > 0 ? 127 + 46 * rows : 210;
  }

  public showRefreshingRows(): void {
    this.refreshingRows = true;
  }

  public skeletonRowCount(group: FreightRateGroup): number {
    return Math.max(group.freightRates.length, 1);
  }

  public shouldValidateRowSelection(): boolean {
    return !(this.quote() instanceof AirTariffQuote);
  }

  public isRowSelectable(freightRate: FreightRate): boolean {
    if (
      freightRate.rate.getSource() === rateSource.online &&
      !!freightRate.failedRestrictions
    ) {
      return false;
    }

    if (freightRate.rate.getType() === rateType.spot) {
      return true;
    }

    if (
      freightRate.rate instanceof LtlRate ||
      freightRate.rate instanceof LtlLinkedRate
    ) {
      return freightRate.rate.getChargeableWeightPrice() > 0;
    }

    if (
      freightRate.rate instanceof LclRate ||
      freightRate.rate instanceof AirRate
    ) {
      return (
        freightRate.rate.getChargeableWeightPrice() != 0 ||
        freightRate.rate.getBlPrice() != 0
      );
    }

    return true;
  }

  public sortRates(groupName: string, fieldName: string, order: number): void {
    this.freightRateGroups = this.freightRateGroupService.sortRates(
      this.freightRateGroups,
      groupName,
      fieldName,
      order,
    );
    this.refreshDataTables();
  }

  public isBatchServiceSelectVisible(): boolean {
    return (
      !!this.freightRateGroups && Object.keys(this.freightRateGroups).length > 0
    );
  }

  // A refresh normally clears the list and shows the loading skeleton. That is
  // right when the lanes changed, but a surcharge toggle only moves prices, and
  // the default skeleton has a fixed row count, which resizes the page.
  // `keepRows` therefore builds the new state off to the side and commits it in
  // one go once the rates arrive; meanwhile `refreshingRows` shows a skeleton
  // of the rows already on screen.
  public fetchFreightRates(keepRows = false): void {
    this.fetchDeferred = false;
    this.refreshingRows = false;

    if (!keepRows) {
      this.freightRateGroups = {};
    }

    const quote = this.quote();
    if (!(quote instanceof BaseStandardQuote)) {
      return;
    }

    this.loading = !keepRows;

    const { groups, commit } = this.buildFreightRateGroups(quote);

    const canFetch =
      !!quote.getOriginLocations() &&
      !!quote.getDestinationLocations() &&
      quote.getId() !== 0;

    if (!keepRows || !canFetch) {
      commit();
    }

    if (!canFetch) {
      return;
    }

    this.refreshingRows = keepRows;

    // Cancel previous requests
    if (this.rateListSubscription) {
      this.rateListSubscription.unsubscribe();
    }
    this.rateListSubscription = this.rateRepository
      .getFreightRate(quote.getId())
      .subscribe({
        next: (rawList) => {
          if (keepRows) {
            commit();
          }
          this.refreshingRows = false;
          this.freightRateGroups = {
            ...this.freightRateGroupService.groupRates(groups, rawList),
          };
          this.checkForExternalLinks();
          this.dropOutdatedPartnerRates();
          this.checkForRoutes();
          this.checkForTiers();
          this.restoreOnlineRows();
          this.refreshDataTables();
          this.loadPartnerRates();
        },
        error: () => {
          // Never leave stale rows, or a stuck skeleton, behind a failed
          // refresh - `complete` does not run after `error`.
          if (keepRows) {
            commit();
          }
          this.loading = false;
          this.refreshingRows = false;
          this.notifyService.toast(TOAST.ERROR, "Rates were not fetched");
        },
        complete: () => {
          this.loading = false;
          this.refreshingRows = false;
        },
      });
  }

  // Puts the online rows each lane loaded within the hour back on the rebuilt
  // groups. Rows the user has since selected are left out: selecting one saves
  // it, so the rate list returns it and the cached copy would show it twice.
  private restoreOnlineRows(): void {
    const quoteId = this.quote().getId();
    Object.values(this.freightRateGroups)
      .filter((group) => group.canLoadOnlineRates)
      .forEach((group) => {
        const rows = this.onlineRateCache
          .rows(quoteId, this.partnerLaneKey(group))
          .filter((onlineRate) => !onlineRate.getRate().getId());
        if (rows.length > 0) {
          group.freightRates = [...group.freightRates, ...rows];
        }
      });
  }

  private showFreightRateSkeleton(): void {
    this.freightRateGroups = {};

    const quote = this.quote();
    if (!(quote instanceof BaseStandardQuote)) {
      return;
    }

    this.loading = true;
    this.buildFreightRateGroups(quote).commit();
  }

  private buildFreightRateGroups(quote: BaseStandardQuote): {
    groups: Record<string, FreightRateGroup>;
    commit: () => void;
  } {
    const groups = this.freightRateGroupService.createGroups(
      quote.getOriginLocations(),
      quote.getDestinationLocations(),
      quote,
    );
    const groupNames = this.freightRateGroupService.createGroupNames(groups);

    Object.keys(groups).forEach((key) => {
      groups[key].columns = this.columns;
      // The header used to split the group name on every pass; the split only
      // changes when the groups are rebuilt, so do it here instead.
      const [originLabel, destinationLabel] = key.split(this.groupSep);
      groups[key].originLabel = originLabel;
      groups[key].destinationLabel = destinationLabel;
    });

    this.updateNewFreightRates(groups);

    const commit = (): void => {
      // Seeded here rather than where the groups are built: with `keepRows`
      // the carrier list may land between the two.
      this.seedOnlineCarriers(groups);
      this.freightRateGroups = groups;
      this.freightRateGroupNames = groupNames;
      // A lane that left the screen is seen afresh once it is back.
      this.seenLanes = new Set(
        groupNames.filter((name) => this.seenLanes.has(name)),
      );
      this.updateColumns();
    };

    return { groups, commit };
  }

  public onBatchSelectionChange(change: {
    selected: FreightRate[];
    deselected: FreightRate[];
  }): void {
    const requests = [
      this.createRoutes(change.selected),
      this.deleteRoutes(change.deselected),
    ].filter((request) => !!request);
    if (requests.length === 0) {
      return;
    }

    this.batchSelectionSaving = true;
    merge(...requests)
      .pipe(
        finalize(() => {
          this.batchSelectionSaving = false;
          // Ticks what was saved: the new selection, or the previous one where
          // the request failed.
          this.batchSelect()?.getSelectedContracts();
        }),
      )
      .subscribe();
  }

  public onBatchMarginUpdate(routes: Route[]): void {
    this.batchMarginSaving = true;
    this.emitQuoteUpdating();
    this.routeRepository
      .updateBatch(this.quote().getId(), routes)
      .pipe(finalize(() => (this.batchMarginSaving = false)))
      .subscribe({
        next: () => this.onRoutesSaved("Successfully updated routes"),
        error: () => {
          this.emitQuoteUpdated();
          this.notifyService.toast(TOAST.ERROR, "Error with updating routes");
        },
      });
  }

  // It should be possible to edit only draft spot rates for all rates except LTL
  public canEditRate(user: User, freightRate: FreightRate): boolean {
    return (
      freightRate.getRate().getType() === rateType.spot &&
      freightRate.getRate().getStatus() === rateStatus.draft
    );
  }

  // Resolved on quote load rather than read through a template call, so the
  // group header does not re-test the quote type once per group per pass.
  private updateExportable(): void {
    this.exportable = this.quote() instanceof LtlQuote;
  }

  public export() {
    this.rateRepository.exportByQuote(this.quote()).subscribe((fileUrl) => {
      window.location.href = fileUrl;
    });
  }

  public onLoadOnlineCarrier(
    freightRateGroup: FreightRateGroup,
    type: string,
  ): void {
    if (type === onlineCarrierType.maersk) {
      this.selectMaerskLocations(freightRateGroup);
    } else if (
      type === onlineCarrierType.other &&
      this.quote() instanceof FclQuote
    ) {
      this.loadOkargoRows(freightRateGroup);
    } else {
      this.loadOnlineCarrierRows(freightRateGroup, type);
    }
  }

  // One message at a time: a Search all can run into the allowance on every lane.
  public onSearchAllowanceReached(): void {
    if (this.searchAllowanceMessageOpen) {
      return;
    }
    this.searchAllowanceMessageOpen = true;
    this.searchAllowanceMessage()
      .pipe(finalize(() => (this.searchAllowanceMessageOpen = false)))
      .subscribe(noop);
  }

  private searchAllowanceMessage(): Observable<void> {
    const organizationId = this.searchPurchaseOrganizationId();
    if (organizationId === null) {
      return this.notifyService.alert(
        `${searchAllowanceReachedMessage} Contact your Company Admin to get more searches.`,
        "",
      );
    }

    return this.notifyService
      .confirm(
        `${searchAllowanceReachedMessage} Get more searches to continue using Live Rates.`,
        "",
        { confirmLabel: "Get More Searches", denyLabel: "Close" },
      )
      .pipe(tap(() => this.openIntegrations(organizationId)));
  }

  // Only a Company Admin can confirm a purchase.
  private searchPurchaseOrganizationId(): number | null {
    const canPurchase =
      !!this.user?.hasRole(role.superAdmin) ||
      !!this.user?.hasRole(role.organizationAdmin);

    return canPurchase ? (this.user.organization?.getId() ?? null) : null;
  }

  private openIntegrations(organizationId: number): void {
    this.router.navigate(["organization", organizationId], {
      queryParams: { tab: organizationTab.integrations },
    });
  }

  public onLoadMaerskRows(
    freightRateGroup: FreightRateGroup,
    originGeoId: string = null,
    destinationGeoId: string = null,
  ): void {
    this.loadOnlineCarrierRows(
      freightRateGroup,
      onlineCarrierType.maersk,
      originGeoId,
      destinationGeoId,
    );
  }

  private loadOnlineCarrierRows(
    freightRateGroup: FreightRateGroup,
    type: string,
    originGeoId: string = null,
    destinationGeoId: string = null,
  ): void {
    // Read before subscribing: `complete` drops the entry from the lane.
    // The generic "other" label ("Check rates online") names no carrier.
    const label =
      type === onlineCarrierType.other
        ? null
        : freightRateGroup.onlineCarriers.find(
            (carrier) => carrier.type === type,
          )?.label;
    const noRatesMessage = label
      ? `${label}: There are no online rates`
      : "There are no online rates";
    const quoteId = this.quote().getId();
    const laneKey = this.partnerLaneKey(freightRateGroup);
    const paid = this.isPaidCarrier(type);
    this.patchOnlineCarrier(freightRateGroup, type, { loading: true });
    this.updateLoadingInProgress();
    this.rateListSubscription = this.rateRepository
      .getOnlineFreightRate(
        quoteId,
        freightRateGroup.originLocation.getLocation(),
        freightRateGroup.destinationLocation.getLocation(),
        type,
        originGeoId,
        destinationGeoId,
      )
      // A load the closed tab left behind is asked again on the new one; its
      // answer would be cached a second time.
      .pipe(
        retry({
          count: RENEWING_POLLS,
          delay: (error: unknown, attempt: number) =>
            this.awaitRenewal(freightRateGroup, type, error, attempt),
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (rawList) => {
          // An empty answer too: the carrier was asked and stays asked.
          this.onlineRateCache.store(quoteId, laneKey, type, rawList);
          freightRateGroup.freightRates = [
            ...freightRateGroup.freightRates,
            ...rawList,
          ];
          this.refreshDataTables();
          if (rawList.length == 0) {
            this.notifyService.toast(TOAST.INFO, noRatesMessage);
          }
        },
        error: (error: unknown) => {
          if (error instanceof OnlineCarrierRenewingError) {
            this.notifyService.toast(
              TOAST.WARN,
              "Still reconnecting, try again in a few minutes",
            );
          } else if (
            paid &&
            error instanceof HttpErrorResponse &&
            error.status === noSearchCreditsStatus
          ) {
            this.onSearchAllowanceReached();
          } else {
            this.notifyService.toast(TOAST.ERROR, "Rates were not fetched");
          }
          this.patchOnlineCarrier(freightRateGroup, type, {
            loading: false,
            loadingLabel: null,
          });
          this.updateLoadingInProgress();
          this.refreshSearchCredits(paid);
        },
        complete: () => {
          this.removeOnlineCarrier(freightRateGroup, type);
          this.updateLoadingInProgress();
          this.refreshSearchCredits(paid);
        },
      });
  }

  // A paid search may have cost a credit, or found the balance spent.
  private refreshSearchCredits(paid: boolean): void {
    if (paid) {
      this.fetchSearchCredits();
    }
  }

  // Only a renewing connection is asked again: the lane waits on its button
  // until the background reconnect lands, without a page reload.
  private awaitRenewal(
    freightRateGroup: FreightRateGroup,
    type: string,
    error: unknown,
    attempt: number,
  ): Observable<number> {
    if (!(error instanceof OnlineCarrierRenewingError)) {
      return throwError(() => error);
    }
    if (attempt === 1) {
      this.notifyService.toast(TOAST.INFO, error.message);
      this.patchOnlineCarrier(freightRateGroup, type, {
        loadingLabel: "Reconnecting...",
      });
    }

    return timer(RENEWING_POLL_MS);
  }

  private loadOkargoRows(freightRateGroup: FreightRateGroup): void {
    const other = onlineCarrierType.other;
    this.patchOnlineCarrier(freightRateGroup, other, { loading: true });
    this.updateLoadingInProgress();
    this.userService.getUser().then((user) => {
      const okargoCarriers = user?.organization?.getOkargoCarriers();

      if (!okargoCarriers || Object.keys(okargoCarriers).length === 0) {
        this.patchOnlineCarrier(freightRateGroup, other, { loading: false });
        this.updateLoadingInProgress();
        this.notifyService.toast(TOAST.INFO, "No online carriers configured");
        return;
      }

      const carrierDisplayNames: Record<string, string> = {
        HMM1: "Hyundai",
        CCL: "Cosco",
        ZIM2: "ZIM",
        HPL2: "HPL",
        CMA2: "CMA CGM Contracts",
        CMA4: "CMA CGM SpotOn",
      };
      const carrierEntries = Object.entries(okargoCarriers);
      const pendingNames = carrierEntries.map(
        ([key, _]) => carrierDisplayNames[key] ?? key,
      );
      const total = pendingNames.length;

      this.patchOnlineCarrier(freightRateGroup, other, {
        loadingLabel: `Loading (0/${total}): ${pendingNames.join(", ")}`,
      });

      const updateProgress = (code: string) => {
        const displayName = carrierDisplayNames[code] ?? code;
        const idx = pendingNames.indexOf(displayName);
        if (idx !== -1) {
          pendingNames.splice(idx, 1);
        }
        const loaded = total - pendingNames.length;
        this.patchOnlineCarrier(freightRateGroup, other, {
          loadingLabel:
            pendingNames.length > 0
              ? `Loading (${loaded}/${total}): ${pendingNames.join(", ")}`
              : null,
        });
      };

      const quoteId = this.quote().getId();
      const laneKey = this.partnerLaneKey(freightRateGroup);
      const requests$ = carrierEntries.map(([code, name]) =>
        this.rateRepository
          .getOnlineFreightRate(
            quoteId,
            freightRateGroup.originLocation.getLocation(),
            freightRateGroup.destinationLocation.getLocation(),
            other,
            null,
            null,
            code,
          )
          .pipe(
            tap((rawList) => {
              updateProgress(code);
              freightRateGroup.freightRates = [
                ...freightRateGroup.freightRates,
                ...rawList,
              ];
              if (rawList.length === 0) {
                this.notifyService.toast(
                  TOAST.INFO,
                  `${carrierDisplayNames[code] ?? code}: There are no online rates`,
                  null,
                  true,
                );
              }
              this.refreshDataTables();
            }),
            catchError((err) => {
              updateProgress(code);
              this.notifyService.toast(
                TOAST.ERROR,
                `Failed to fetch rates for ${carrierDisplayNames[code] ?? code}`,
                <string>new BackendErrorPipe().transform(err),
                true,
              );
              return of([] as FreightRate[]);
            }),
          ),
      );

      // No summary toast: every carrier already reported its own result.
      // Cached once every carrier has answered, so a run the closed tab cut
      // short is offered again in full.
      forkJoin(requests$)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (rawLists) =>
            this.onlineRateCache.store(
              quoteId,
              laneKey,
              other,
              rawLists.flat(),
            ),
          complete: () => {
            this.removeOnlineCarrier(freightRateGroup, other);
            this.updateLoadingInProgress();
          },
        });
    }, noop);
  }

  // While one lane loads the `other` carrier, its button is blocked on every
  // lane. Call after any change to that entry's `loading`.
  private updateLoadingInProgress(): void {
    const other = onlineCarrierType.other;
    const groups = Object.values(this.freightRateGroups);
    const disabled = groups.some((group) =>
      group.onlineCarriers.some(
        (carrier) => carrier.type === other && carrier.loading,
      ),
    );
    groups.forEach((group) =>
      this.patchOnlineCarrier(group, other, { disabled }),
    );
  }

  public onLaneSeen(groupName: string): void {
    this.seenLanes.add(groupName);
    if (this.partnerRatesScheduled) {
      return;
    }

    // Each lane reports on its own, and the ones in view on open do so in the
    // same frame; gathering them keeps them one pass, with one toast.
    this.partnerRatesScheduled = true;
    setTimeout(() => {
      this.partnerRatesScheduled = false;
      // A rate list still on its way rebuilds the groups and then loads every
      // lane seen by that time.
      if (
        this.loading ||
        this.fetchDeferred ||
        (this.rateListSubscription && !this.rateListSubscription.closed)
      ) {
        return;
      }
      this.loadPartnerRates();
    });
  }

  // Partner rates used to wait for a button press; they are fetched for every
  // eligible lane in view, once the rate list resolves or once the lane is
  // scrolled into view. Since the user asked for nothing, the lanes report as
  // one - a quote with twenty of them would otherwise raise twenty toasts.
  //
  // The request carries the two location codes and the PartnerRateCriteria, so
  // a lane is worth fetching only the first time that key is seen. Every other
  // edit - the client, a surcharge, a note - re-runs fetchFreightRates() and
  // would otherwise re-issue an identical request per lane. The same goes for a
  // lane whose request is still in flight: it is left to that request.
  private loadPartnerRates(): void {
    // Partner rates are FCL, LCL and AIR only. createGroups() already encodes
    // that in canLoadPartnerOnlineRates, so the check belongs here - once per
    // pass - rather than per lane, where an early return would strand a group
    // with the flag still true and no subscription left to clear it.
    const eligible = PartnerRateCriteria.fromQuote(this.quote())
      ? Object.entries(this.freightRateGroups)
          .filter(
            ([name, group]) =>
              group.canLoadPartnerOnlineRates && this.seenLanes.has(name),
          )
          .map(([, group]) => group)
      : [];
    const keys = new Map(
      eligible.map((group) => [group, this.partnerLaneKey(group)]),
    );

    // A changed leg or criterion yields a new key, so the old request is for a
    // lane no longer on screen.
    const onScreen = new Set(keys.values());
    this.partnerRateRequests.forEach((request, key) => {
      if (!onScreen.has(key)) {
        request.subscription.unsubscribe();
        this.partnerRateRequests.delete(key);
        this.settlePartnerRatePass(request.pass);
      }
    });

    const lanes: FreightRateGroup[] = [];
    let restored = false;
    eligible.forEach((group) => {
      const key = keys.get(group);
      if (this.partnerRatesByLane.has(key)) {
        restored = this.restorePartnerRows(group) || restored;
      } else if (this.partnerRateRequests.has(key)) {
        // The rebuilt group stands in for the one the request was made for.
        group.loadingPartnerOnlineRates = true;
      } else {
        lanes.push(group);
      }
    });
    if (restored) {
      this.refreshDataTables();
    }

    // Nothing was asked of the partner, so there is nothing to report.
    if (lanes.length === 0) {
      return;
    }

    const pass: PartnerRatePass = { pending: lanes.length, failed: 0 };
    lanes.forEach((lane) => this.loadPartnerRows(lane, keys.get(lane), pass));
  }

  // What the partner endpoint is addressed by, and therefore what makes two
  // passes over the same lane the same request. It is also what a selected
  // partner rate is remembered by, to tell later whether it still applies, and
  // what the lane's online rows are cached by.
  private partnerLaneKey(freightRateGroup: FreightRateGroup): string {
    return JSON.stringify({
      origin: freightRateGroup.originLocation.getLocation().getCode(),
      originKind: freightRateGroup.originLocation.getLocation().getKind(),
      destination: freightRateGroup.destinationLocation.getLocation().getCode(),
      destinationKind: freightRateGroup.destinationLocation
        .getLocation()
        .getKind(),
      ...PartnerRateCriteria.fromQuote(this.quote()),
    });
  }

  // A partner rate is priced for the request it came from, so once the quote
  // asks for anything else - a weight, a package, the equipment, the validity -
  // it no longer applies: a selected one is unselected, every one leaves the
  // lane, and loadPartnerRates() fetches the rates for the new request. A sent
  // quote keeps the rates it was sent with.
  private dropOutdatedPartnerRates(): void {
    if (this.quote().getStatus() !== quoteStatus.draft) {
      return;
    }

    Object.values(this.freightRateGroups)
      .filter((group) => group.canLoadPartnerOnlineRates)
      .forEach((group) => {
        const key = this.partnerLaneKey(group);
        const outdated = group.freightRates.filter((freightRate) => {
          const criteria = this.partnerRateSelection.criteriaOf(
            freightRate.getRate().getId(),
          );

          return !!criteria && criteria !== key;
        });
        if (outdated.length === 0) {
          return;
        }

        group.freightRates = group.freightRates.filter(
          (freightRate) => !outdated.includes(freightRate),
        );
        outdated.forEach((freightRate) =>
          this.partnerRateSelection.forget(freightRate.getRate().getId()),
        );
        outdated
          .filter((freightRate) => freightRate.haveRoute())
          .forEach((freightRate) => this.deleteRoute(freightRate, false));
      });
  }

  // The request a fetched partner row was priced for, or null for any other row.
  private partnerCriteriaOf(freightRate: FreightRate): string {
    for (const [key, rows] of this.partnerRatesByLane) {
      if (rows.includes(freightRate)) {
        return key;
      }
    }

    return null;
  }

  // Puts a known lane's rows back on the rebuilt group. Rows the user has since
  // selected are left out: selecting one persists it, so from then on the rate
  // list returns it and appending the cached copy beside it would show the same
  // offering twice.
  private restorePartnerRows(freightRateGroup: FreightRateGroup): boolean {
    freightRateGroup.canLoadPartnerOnlineRates = false;

    const rows = this.partnerRatesByLane
      .get(this.partnerLaneKey(freightRateGroup))
      .filter((partnerRate) => !partnerRate.getRate().getId());
    if (rows.length === 0) {
      return false;
    }

    freightRateGroup.freightRates = [...freightRateGroup.freightRates, ...rows];

    return true;
  }

  private loadPartnerRows(
    freightRateGroup: FreightRateGroup,
    key: string,
    pass: PartnerRatePass,
  ): void {
    freightRateGroup.loadingPartnerOnlineRates = true;
    const subscription = this.rateRepository
      .getPartnerFreightRate(
        freightRateGroup.originLocation.getLocation(),
        freightRateGroup.destinationLocation.getLocation(),
        PartnerRateCriteria.fromQuote(this.quote()),
      )
      .subscribe({
        next: (rawList) => {
          // Answered, so the lane is known from here on - including an empty
          // answer, which is as final as a full one.
          this.partnerRatesByLane.set(key, rawList);
          // A refresh since the request may have rebuilt the group; the rows
          // belong on the one on screen now. None on screen yet - a refresh
          // still waiting for its rate list - and that refresh serves them
          // from the cache.
          const lane = this.partnerLaneGroup(key);
          if (lane && this.restorePartnerRows(lane)) {
            this.refreshDataTables();
          }
        },
        error: () => {
          // Nothing was cached here, so a failed lane is the one case the next
          // refresh does re-request.
          pass.failed += 1;
          this.settlePartnerLane(key, pass);
        },
        complete: () => this.settlePartnerLane(key, pass),
      });
    // A synchronous answer has settled the lane already.
    if (!subscription.closed) {
      this.partnerRateRequests.set(key, { subscription, pass });
    }
  }

  // The group on screen that waits for the lane's partner request.
  private partnerLaneGroup(key: string): FreightRateGroup {
    return Object.values(this.freightRateGroups).find(
      (group) =>
        group.loadingPartnerOnlineRates && this.partnerLaneKey(group) === key,
    );
  }

  private settlePartnerLane(key: string, pass: PartnerRatePass): void {
    const lane = this.partnerLaneGroup(key);
    this.partnerRateRequests.delete(key);
    if (lane) {
      lane.loadingPartnerOnlineRates = false;
      // The flag means "this group still has partner rates to serve", so it
      // is cleared once the request settles either way. It only spans the
      // current group set - every refresh rebuilds the groups through
      // createGroups(), which sets it afresh.
      lane.canLoadPartnerOnlineRates = false;
    }
    this.settlePartnerRatePass(pass);
  }

  // Called once per lane, including a lane canceled because it left the
  // screen; only the last one to settle reports. A lane a refresh carried over
  // still settles the pass that asked for it, so a refresh adds no toast of
  // its own for it.
  private settlePartnerRatePass(pass: PartnerRatePass): void {
    pass.pending -= 1;
    if (pass.pending > 0) {
      return;
    }

    // A lane with nothing to offer says nothing: partner rates are unused by
    // most organizations, and the pass was never asked for. Only an outage is
    // worth interrupting for, since it hides rates that do exist.
    if (pass.failed > 0) {
      this.notifyService.toast(TOAST.ERROR, "Partner rates were not fetched");
    }
  }

  private createRoute(freightRate: FreightRate) {
    const rate = freightRate.getRate();
    if (freightRate.haveRoute() || freightRate.isBusy) {
      return;
    }
    this.assignDefaultRoute(freightRate);
    const quote = this.quote();
    if (
      rate instanceof LtlRate ||
      rate instanceof LtlLinkedRate ||
      rate instanceof FtlFlatRate ||
      rate instanceof PtlFlatRate
    ) {
      this.emitQuoteUpdating();
      freightRate.isBusy = true;
      this.routeRepository
        .createFlat(
          quote.getId(),
          freightRate.getFlatRateRouteId(),
          freightRate.getPickupRateRouteId(),
          freightRate.getDeliveryRateRouteId(),
          freightRate.getRoute(),
          !(rate instanceof FtlFlatRate || rate instanceof PtlFlatRate)
            ? freightRate.getQuoteOriginLocationId()
            : null,
          !(rate instanceof FtlFlatRate || rate instanceof PtlFlatRate)
            ? freightRate.getQuoteDestinationLocationId()
            : null,
          freightRate.getRate().getId(),
        )
        .subscribe({
          next: (route) => {
            freightRate.isBusy = false;
            freightRate.setRoute(route);
            this.checkForRoutes();
            this.checkForTiers();
            this.refreshDataTables();
            this.emitQuoteUpdated();
            this.notifyService.toast(TOAST.SUCCESS, "Route has been created");
            this.eventService.eventBus.emit({
              event: eventType.quoteAverageMarginUpdated,
            });
          },
          error: (err: HttpErrorResponse) => {
            this.onRouteCreateFailed([freightRate], err);
          },
        });
    } else if (rate instanceof BaseLinkedRate) {
      this.emitQuoteUpdating();
      freightRate.isBusy = true;
      this.routeRepository
        .create(
          quote.getId(),
          rate.getOriginLocation().getId(),
          rate.getDestinationLocation().getId(),
          freightRate.getFlatRateRouteId(),
          freightRate.getRoute(),
        )
        .subscribe({
          next: (route) => {
            freightRate.isBusy = false;
            freightRate.setRoute(route);
            this.checkForRoutes();
            this.checkForTiers();
            this.refreshDataTables();
            this.emitQuoteUpdated();
            this.notifyService.toast(TOAST.SUCCESS, "Route has been created");
            this.eventService.eventBus.emit({
              event: eventType.quoteAverageMarginUpdated,
            });
          },
          error: (err: HttpErrorResponse) => {
            this.onRouteCreateFailed([freightRate], err);
          },
        });
    }
  }

  // Null when there is nothing to create; the request settles either way.
  private createRoutes(freightRates: FreightRate[]): Observable<unknown> {
    const pending = freightRates.filter(
      (freightRate) =>
        !freightRate.haveRoute() &&
        !freightRate.isBusy &&
        freightRate.getRate() instanceof BaseLinkedRate,
    );
    if (pending.length === 0) {
      return null;
    }

    const items = pending.map((freightRate): RouteBatchItem => {
      this.assignDefaultRoute(freightRate);
      freightRate.isBusy = true;
      const rate = freightRate.getRate() as BaseLinkedRate;

      return {
        route: freightRate.getRoute(),
        flatRateRouteId: freightRate.getFlatRateRouteId(),
        originLocationId: rate.getOriginLocation().getId(),
        destinationLocationId: rate.getDestinationLocation().getId(),
      };
    });
    this.emitQuoteUpdating();

    return this.routeRepository.createBatch(this.quote().getId(), items).pipe(
      tap((routes) => {
        pending.forEach((freightRate, i) => {
          freightRate.isBusy = false;
          freightRate.setRoute(routes[i]);
        });
        this.checkForRoutes();
        this.onRoutesSaved("Routes have been created");
      }),
      catchError((err: HttpErrorResponse) => {
        this.onRouteCreateFailed(pending, err);

        return EMPTY;
      }),
    );
  }

  // Null when there is nothing to delete; the request settles either way.
  private deleteRoutes(freightRates: FreightRate[]): Observable<unknown> {
    const selected = freightRates.filter((freightRate) =>
      freightRate.haveRoute(),
    );
    if (selected.length === 0) {
      return null;
    }

    const routes = selected.map((freightRate) => freightRate.getRoute());
    selected.forEach((freightRate) => freightRate.setRoute(null));
    this.checkForRoutes();
    this.emitQuoteUpdating();

    return this.routeRepository.deleteBatch(this.quote().getId(), routes).pipe(
      tap(() => this.onRoutesSaved("Routes have been deleted")),
      catchError(() => {
        // Nothing was deleted, so the rows get their routes back.
        selected.forEach((freightRate, i) => freightRate.setRoute(routes[i]));
        this.checkForRoutes();
        this.refreshDataTables();
        this.emitQuoteUpdated();
        this.notifyService.toast(TOAST.ERROR, "Routes have not been deleted");

        return EMPTY;
      }),
    );
  }

  private onRoutesSaved(message: string): void {
    this.checkForTiers();
    this.refreshDataTables();
    this.emitQuoteUpdated();
    this.notifyService.toast(TOAST.SUCCESS, message);
    this.eventService.eventBus.emit({
      event: eventType.quoteAverageMarginUpdated,
    });
  }

  // The route a ticked rate starts from, priced by the rate's own margins.
  private assignDefaultRoute(freightRate: FreightRate): void {
    const rate = freightRate.getRate();
    if (rate instanceof FclRate && !freightRate.getRoute()) {
      freightRate.setRoute(
        RouteMapper.map({
          discriminator: rate.getDiscriminator(),
          containers: rate.getContainerMargins().getRequestParams(),
        }),
      );
    }
    if (rate instanceof FtlFlatRate && !freightRate.getRoute()) {
      freightRate.setRoute(
        RouteMapper.map({
          discriminator: rate.getDiscriminator(),
          blPrice: rate.getBlMargin(),
        }),
      );
    }
    if (rate instanceof PtlFlatRate && !freightRate.getRoute()) {
      freightRate.setRoute(
        RouteMapper.map({
          discriminator: rate.getDiscriminator(),
          comment: freightRate.comment,
          chargeableWeightPrice: rate.getChargeableWeightPrice(),
          blPrice: rate.getBlPrice(),
        }),
      );
    }
    if (
      (rate instanceof LtlRate ||
        rate instanceof LtlLinkedRate ||
        rate instanceof AirRate ||
        rate instanceof LclRate) &&
      !freightRate.getRoute()
    ) {
      if (this.quote() instanceof AirTariffQuote) {
        freightRate.setRoute(
          RouteMapper.map({
            discriminator: quoteType.airtariff,
            chargeableWeightPrice: rate.getChargeableWeightMargin(),
          }),
        );
      } else {
        freightRate.setRoute(
          RouteMapper.map({
            discriminator: rate.getDiscriminator(),
            chargeableWeightPrice: rate.getChargeableWeightMargin(),
            blPrice: rate instanceof LclRate ? rate.getBlMargin() : null,
          }),
        );
      }
    }
  }

  private deleteRoute(freightRate: FreightRate, notify = true) {
    if (freightRate.haveRoute()) {
      const route = freightRate.getRoute();
      const rate = freightRate.getRate();
      if (route instanceof PtlRoute && rate instanceof PtlFlatRate) {
        rate.setBlPrice(route.getBlPrice());
        rate.setChargeableWeightPrice(route.getChargeableWeightPrice());
        rate.setComment(route.getComment() ?? "");
      }
      freightRate.setRoute(null);
      this.checkForRoutes();
      this.emitQuoteUpdating();
      this.routeRepository.delete(route).subscribe({
        next: (response) => {
          this.checkForRoutes();
          this.checkForTiers();
          this.refreshDataTables();
          this.emitQuoteUpdated();
          if (notify) {
            this.notifyService.toast(TOAST.SUCCESS, "Route has been deleted");
          }
          this.eventService.eventBus.emit({
            event: eventType.quoteAverageMarginUpdated,
          });
        },
        error: (err) => {
          this.notifyService.toast(TOAST.ERROR, "Route has not been deleted");
        },
      });
    }
  }

  private createRate(freightRate: FreightRate): void {
    this.emitQuoteUpdating();
    const rate = freightRate.getRate();
    const quote = this.quote();
    rate.setDiscriminator(quote.getQuoteType());
    rate.setStatus(rateStatus.draft);
    if (quote instanceof AirTariffQuote) {
      rate.setDiscriminator(rateDiscriminator.air);
    }
    this.rateRepository
      .add(
        rate,
        quote,
        freightRate.quoteOriginLocationId,
        freightRate.quoteDestinationLocationId,
      )
      .subscribe({
        next: (updatedRate) => {
          const partnerCriteria = this.partnerCriteriaOf(freightRate);
          if (partnerCriteria) {
            this.partnerRateSelection.remember(
              updatedRate.getId(),
              partnerCriteria,
            );
          }
          freightRate.rate.setId(updatedRate.getId());
          freightRate.rate.setType(updatedRate.getType());
          freightRate.rate.setCreatedBy(updatedRate.getCreatedBy());
          if (
            updatedRate instanceof LtlRate &&
            updatedRate.pickupRate instanceof LtlPickupDeliveryRate &&
            updatedRate.deliveryRate instanceof LtlPickupDeliveryRate
          ) {
            freightRate.flatRateRouteId = updatedRate
              .getFlatRateRoutes()[0]
              .getId();
            freightRate.pickupRateRouteId = updatedRate.pickupRate
              .getFlatRateRoutes()[0]
              .getId();
            freightRate.deliveryRateRouteId = updatedRate.deliveryRate
              .getFlatRateRoutes()[0]
              .getId();
          }
          if (updatedRate instanceof FtlFlatRate) {
            freightRate.flatRateRouteId = updatedRate
              .getFlatRateRoutes()[0]
              .getId();
          }
          if (updatedRate instanceof PtlFlatRate) {
            freightRate.flatRateRouteId = updatedRate
              .getFlatRateRoutes()[0]
              .getId();
          }
          if (
            freightRate.rate instanceof BaseLinkedRate &&
            updatedRate instanceof BaseLinkedRate
          ) {
            freightRate.rate.setOriginLocation(updatedRate.getOriginLocation());
            freightRate.rate.setDestinationLocation(
              updatedRate.getDestinationLocation(),
            );
          }
          if (
            freightRate.rate instanceof LclRate &&
            updatedRate instanceof LclRate
          ) {
            freightRate.rate.setWeightPrice(updatedRate.getWeightPrice());
            freightRate.rate.setMeasurementPrice(
              updatedRate.getMeasurementPrice(),
            );
          }
          this.checkForExternalLinks();
          this.createRoute(freightRate);
          this.emitQuoteUpdated();
          this.notifyService.toast(TOAST.SUCCESS, "Successfully created rate");
        },
        error: (err): void => {
          this.notifyService.toast(TOAST.ERROR, "Rate has not been created");
        },
        complete: (complete?): void => {
          this.refreshDataTables();
        },
      });
  }

  private updateRate(rate: BaseLinkedRate): void {
    this.emitQuoteUpdating();
    this.rateRepository.simpleUpdateFromQuoteFreightTab(rate).subscribe({
      next: (ratePatchSuccess) => {
        this.emitQuoteUpdated();
        this.notifyService.toast(TOAST.SUCCESS, "Successfully upadted rate");
      },
      error: (ratePatchError) =>
        this.notifyService.toast(TOAST.ERROR, "Error with updating rate"),
    });
  }

  private updateRoute(route: Route): void {
    this.emitQuoteUpdating();
    this.routeRepository.update(route).subscribe({
      next: (routePatchSuccess) => {
        this.checkForTiers();
        this.refreshDataTables();
        this.emitQuoteUpdated();
        this.notifyService.toast(TOAST.SUCCESS, "Successfully upadted route");
        this.eventService.eventBus.emit({
          event: eventType.quoteAverageMarginUpdated,
        });
      },
      error: (routePatchError) =>
        this.notifyService.toast(TOAST.ERROR, "Error with updating route"),
    });
  }

  // A rejected tick must leave no trace of a selection: the row stays ticked and
  // stuck busy otherwise, and the database never got the route.
  private onRouteCreateFailed(
    freightRates: FreightRate[],
    err: HttpErrorResponse,
  ): void {
    freightRates.forEach((freightRate) => {
      freightRate.isBusy = false;
      freightRate.setRoute(null);
    });
    this.checkForRoutes();
    this.emitQuoteUpdated();

    if (err?.status === rateNoLongerAvailableStatus) {
      this.notifyService.toast(
        TOAST.WARN,
        "This rate is no longer available",
        "Rates have changed since this list was loaded. Refresh the quote to see what can be quoted now.",
        true,
      );

      return;
    }

    this.notifyService.toast(
      TOAST.ERROR,
      "Rate could not be selected",
      "Rate could not be selected. Please try again.",
    );
  }

  private emitQuoteUpdating(): void {
    this.eventService.eventBus.emit({
      event: eventType.quoteUpdating,
      data: this.quote(),
    });
  }

  private emitQuoteUpdated(): void {
    this.eventService.eventBus.emit({
      event: eventType.quoteUpdated,
      data: this.quote(),
    });
  }

  private refreshDataTables() {
    this.freightRateGroupNames.map((groupName) => {
      this.freightRateGroups[groupName].freightRates = [
        ...this.freightRateGroups[groupName].freightRates,
      ];
    });
    this.freightRateGroupNames = [...this.freightRateGroupNames];
  }

  private checkForExternalLinks(): void {
    Object.keys(this.freightRateGroups).forEach((key) => {
      const group = this.freightRateGroups[key];
      group.freightRates.map((freightRate) => {
        if (freightRate.getRate().getType() == rateType.spot) {
          const rate = freightRate.getRate();
          freightRate.externalLink = `/rate/${rate.getDiscriminator()}/${rate.getType()}/${rate.getId()}`;
        }
      });
    });
  }

  private checkForRoutes(): void {
    let haveRoute = false;
    let routeCount = 0;
    const rates = [];
    Object.keys(this.freightRateGroups).forEach((key) => {
      const group = this.freightRateGroups[key];
      group.selection = [];
      group.freightRates.map((freightRate) => {
        if (freightRate.haveRoute()) {
          group.selection.push(freightRate);
          rates.push(freightRate.rate);
          // An expired spot rate stays selected and visible, but it cannot
          // price the quote, so it does not satisfy "at least one rate".
          haveRoute = haveRoute || !freightRate.isExpiredSpotRate();
          routeCount++;
        }
      });
    });
    // A frozen quote serves the rate snapshot taken when it was sent, so its rates
    // expire while it stays viewable; it must never fail the "at least one rate" gate.
    const frozen = this.quote().getStatus() !== quoteStatus.draft;

    this.routeDetect.emit({
      detected: haveRoute || frozen,
      quote: this.quote(),
      count: routeCount,
      rates: rates,
    });
  }

  private checkForTiers(): void {
    this.selectedTier = this.freightRateGroupService.calculateTiers(
      this.freightRateGroups,
    );
  }

  private updateNewFreightRates(groups: Record<string, FreightRateGroup>) {
    Object.keys(groups).forEach((key) => {
      const group = groups[key];
      const quote = this.quote();
      let rateDiscriminator = this.quote().getQuoteType();
      if (rateDiscriminator == quoteType.airtariff) {
        rateDiscriminator = quoteType.air;
      }
      const defaultValidFrom =
        quote.getValidFrom() && quote.getQuoteType() === quoteType.ptl
          ? format(quote.getValidFrom(), "yyyy-MM-dd")
          : format(new Date(), "yyyy-MM-dd");
      const defaultValidTill =
        quote.getValidTo() && quote.getQuoteType() === quoteType.ptl
          ? format(quote.getValidTo(), "yyyy-MM-dd")
          : format(addWeeks(new Date(), 2), "yyyy-MM-dd");
      const defaultCurrency =
        quote.getQuoteType() === quoteType.ptl
          ? currencies.eur.value
          : currencies.usd.value;

      const rate = <BaseLinkedRate>RateMapper.map({
        name: "Spot",
        discriminator: rateDiscriminator,
        createdBy: this.user,
        updatedBy: this.user,
        type: rateType.spot,
        status: rateStatus.draft,
        validFrom: defaultValidFrom,
        validTill: defaultValidTill,
        originLocation: group.originLocation,
        destinationLocation: group.destinationLocation,
        currency: defaultCurrency,
        chargeableWeightPrice: 0,
      });

      const route = RouteMapper.map({
        discriminator: this.quote().getQuoteType(),
        tier: this.selectedTier,
        weightPrice: 0,
        chargeableWeightPrice: 0,
      });

      if (
        rate instanceof FclRate &&
        route instanceof FclRoute &&
        quote instanceof FclQuote
      ) {
        rate.setEquipment(this.equipment());
        rate.getEquipment().map((containerName) => {
          rate.getContainers().setPrice(containerName, 0);
          route.getContainers().setPrice(containerName, 0);
        });
      }

      if (
        (rate instanceof LclRate && quote instanceof LclQuote) ||
        (rate instanceof AirRate && quote instanceof AirQuote)
      ) {
        rate.setChargeableWeight(quote.getChargeableWeight());
      }

      group.newFreightRate = new FreightRate(rate, route);
      group.newFreightRate.quoteOriginLocationId = group.quoteOriginLocationId;
      group.newFreightRate.quoteDestinationLocationId =
        group.quoteDestinationLocationId;
    });
  }

  /************ FCL **************/
  private getFclNetPrice(
    freightRate: FreightRate,
    containerName: ContainerName,
  ): number {
    const rate = freightRate.getRate();
    if (rate instanceof FclRate) {
      return rate.getContainers().getPrice(containerName);
    }
  }

  private getFclMarginPrice(
    freightRate: FreightRate,
    containerName: ContainerName,
  ) {
    const route = freightRate.getRoute();
    if (route instanceof FclRoute) {
      return route.getContainers().getPrice(containerName);
    }
  }

  private setFclNetPrice(
    freightRate: FreightRate,
    containerName: ContainerName,
    value: number,
  ) {
    const rate = freightRate.getRate();
    if (rate instanceof FclRate) {
      rate.getContainers().setPrice(containerName, value);
    }
  }

  private setFclMarginPrice(
    freightRate: FreightRate,
    containerName: ContainerName,
    value: number,
  ) {
    const route = freightRate.getRoute();
    if (route instanceof FclRoute) {
      route.getContainers().setPrice(containerName, value);
    }
  }

  private getFclColumns(): ITableColumn[] {
    const columns = [];
    const equipment = this.equipment();
    if (this.quote() instanceof FclQuote && equipment) {
      const fclColumns = new SortEquipmentPipe().transform(equipment);
      fclColumns.map((containerName) => {
        columns.push({
          header: new FormatContainerLabelPipe().transform(containerName),
          type: tableColumn.priceBox,
          field: containerName,
          sortable: true,
          hideWhenEmpty: false,
          getNet: this.getFclNetPrice,
          setNet: this.setFclNetPrice,
          getMargin: this.getFclMarginPrice,
          setMargin: this.setFclMarginPrice,
        });
      });
    }

    return columns;
  }

  /*********** LCL ************/
  private getWeightNetPrice(freightRate: FreightRate, field: string): number {
    const rate = freightRate.getRate();
    if (
      rate instanceof LclRate ||
      rate instanceof AirRate ||
      rate instanceof LtlRate ||
      rate instanceof LtlLinkedRate
    ) {
      return rate.getChargeableWeightPrice();
    }
  }

  private setWeightNetPrice(
    freightRate: FreightRate,
    field: string,
    value: number,
  ): void {
    const rate = freightRate.getRate();
    if (
      rate instanceof LclRate ||
      rate instanceof AirRate ||
      rate instanceof LtlRate ||
      rate instanceof LtlLinkedRate
    ) {
      rate.setChargeableWeightPrice(value);
    }
  }

  private getWeightMarginPrice(
    freightRate: FreightRate,
    field: string,
  ): number {
    const route = freightRate.getRoute();
    if (
      route instanceof LclRoute ||
      route instanceof AirRoute ||
      route instanceof LtlRoute ||
      route instanceof PtlRoute
    ) {
      return route.getChargeableWeightPrice();
    }
  }

  private setWeightMarginPrice(
    freightRate: FreightRate,
    field: string,
    value: number,
  ): void {
    const route = freightRate.getRoute();
    if (
      route instanceof LclRoute ||
      route instanceof AirRoute ||
      route instanceof LtlRoute ||
      route instanceof PtlRoute
    ) {
      route.setChargeableWeightPrice(value);
    }
  }

  private getWeightMultiplier(freightRate: FreightRate, field: string): number {
    const rate = freightRate.getRate();
    if (rate instanceof LclRate || rate instanceof AirRate) {
      return rate.getChargeableWeight();
    }
  }

  private getShipmentNetPrice(freightRate: FreightRate, field: string): number {
    const rate = freightRate.getRate();
    if (
      rate instanceof LclRate ||
      rate instanceof AirRate ||
      rate instanceof FtlFlatRate ||
      rate instanceof PtlFlatRate
    ) {
      return rate.getBlPrice();
    }
  }

  private setShipmentNetPrice(
    freightRate: FreightRate,
    field: string,
    value: number,
  ): void {
    const rate = freightRate.getRate();
    if (
      rate instanceof LclRate ||
      rate instanceof AirRate ||
      rate instanceof FtlFlatRate ||
      rate instanceof PtlFlatRate
    ) {
      rate.setBlPrice(value);
    }
  }

  private getShipmentMarginPrice(
    freightRate: FreightRate,
    field: string,
  ): number {
    const rate = freightRate.getRate();
    const route = freightRate.getRoute();
    if (rate instanceof PtlFlatRate) {
      if (route instanceof PtlRoute && route.hasChargeableWeightPrice()) {
        return route.getChargeableWeightPrice();
      }

      return rate.getChargeableWeightPrice();
    }

    if (route instanceof PtlRoute) {
      return route.hasChargeableWeightPrice()
        ? route.getChargeableWeightPrice()
        : rate instanceof PtlFlatRate
          ? rate.getChargeableWeightPrice()
          : 0;
    }
    if (
      route instanceof LclRoute ||
      route instanceof AirRoute ||
      route instanceof FtlRoute
    ) {
      return route.getBlPrice();
    }
  }

  private setShipmentMarginPrice(
    freightRate: FreightRate,
    field: string,
    value: number,
  ): void {
    const rate = freightRate.getRate();
    const route = freightRate.getRoute();
    if (rate instanceof PtlFlatRate && !(route instanceof PtlRoute)) {
      rate.setChargeableWeightPrice(value);
      return;
    }

    if (route instanceof PtlRoute) {
      route.setChargeableWeightPrice(value);
      if (rate instanceof PtlFlatRate) {
        rate.setChargeableWeightPrice(value);
      }
      return;
    }
    if (
      route instanceof LclRoute ||
      route instanceof AirRoute ||
      route instanceof FtlRoute
    ) {
      route.setBlPrice(value);
    }
  }

  private getLclColumns(): ITableColumn[] {
    return [
      {
        header: "Chargeable",
        field: "chargeableWeight",
        type: tableColumn.number,
      },
      {
        header: "Ratio",
        field: "conversionRatio",
        type: tableColumn.warning,
      },
      {
        header:
          this.quote() instanceof LclQuote ? "Price (W/M)" : "Price per kg",
        type: tableColumn.priceBox,
        field: "chargeableWeightPrice",
        getNet: this.getWeightNetPrice,
        setNet: this.setWeightNetPrice,
        getMargin: this.getWeightMarginPrice,
        setMargin: this.setWeightMarginPrice,
        getMultiplier: this.getWeightMultiplier,
        sortable: true,
      },
      {
        header: "Price (Shipment)",
        type: tableColumn.priceBox,
        field: "blPrice",
        getNet: this.getShipmentNetPrice,
        setNet: this.setShipmentNetPrice,
        getMargin: this.getShipmentMarginPrice,
        setMargin: this.setShipmentMarginPrice,
        sortable: true,
      },
      { header: "Min Price", field: "minPrice", type: tableColumn.number },
    ];
  }

  /*********** AIR TARIFF ************/
  private getAirTariffColumns(): ITableColumn[] {
    const columns = [];

    const quote = this.quote();
    if (quote instanceof AirTariffQuote) {
      const airTariffColumns = quote.getWeightBrackets();
      Object.keys(airWeightBrackets).map((key) => {
        if (airTariffColumns.indexOf(<AirWeightBracketName>key) !== -1) {
          columns.push({
            header: getWeightBracketsHeader(key),
            type: tableColumn.priceBox,
            field: airWeightBrackets[key],
            getNet: function (rowData, field) {
              return rowData.rate[field];
            },
            setNet: function (rowData, field, value) {
              rowData.rate[field] = value;
            },
            getMargin: function (rowData, field) {
              return rowData.route ? rowData.route[field] : null;
            },
            setMargin: function (rowData, field, value) {
              if (rowData.route) {
                rowData.route[field] = value;
              }
            },
          });
        }
      });
    }

    columns.push({
      header: "Min Price",
      field: "minPrice",
      type: tableColumn.number,
    });

    return columns;
  }

  private updateColumns() {
    this.columns = this.updateColumnsForKind(
      [this.quote().getKind()],
      this.quote().getQuoteType(),
    );
  }

  private updateColumnsForKind(
    kind: string[],
    quoteDiscriminator: string,
  ): ITableColumn[] {
    const isCarrierRequired =
      quoteDiscriminator != quoteType.ltl &&
      quoteDiscriminator != quoteType.ptl;
    let columns: ITableColumn[] = [
      {
        header: "Name",
        field: "name",
        type: tableColumn.default,
        sortable: true,
      },
      {
        header: "",
        field: "warningMessage",
        type: tableColumn.warning,
      },
    ];
    columns = [
      ...columns,
      {
        header: "Service",
        field: "carrier",
        type: tableColumn.service,
        sortable: true,
        required: isCarrierRequired,
        invalid: (freightRate: FreightRate) =>
          isCarrierRequired && !freightRate.carrier,
        displayValue: (freightRate: FreightRate) => freightRate.serviceLabel,
        kind: kind,
      },
    ];

    if (
      quoteDiscriminator != quoteType.ltl &&
      quoteDiscriminator != quoteType.ptl
    ) {
      columns = [
        ...columns,
        {
          header: "Validity",
          field: "validity",
          type: tableColumn.datePicker,
          sortable: true,
          invalid: (freightRate: FreightRate) =>
            freightRate.isExpiredSpotRate(),
          warning: (freightRate: FreightRate) =>
            freightRate.validityWarningMessage,
        },
      ];
    }

    if (quoteDiscriminator == quoteType.fcl) {
      columns = [...columns, ...this.getFclColumns()];
    }

    if (
      quoteDiscriminator == quoteType.lcl ||
      quoteDiscriminator == quoteType.air
    ) {
      columns = [...columns, ...this.getLclColumns()];
    }

    if (quoteDiscriminator == quoteType.airtariff) {
      columns = [...columns, ...this.getAirTariffColumns()];
    }

    if (quoteDiscriminator == quoteType.ltl) {
      columns = [
        ...columns,
        {
          header: "Cost / Margin / Price",
          type: tableColumn.percentBox,
          field: "chargeableWeightPrice",
          getNet: this.getWeightNetPrice,
          setNet: this.setWeightNetPrice,
          getMargin: this.getWeightMarginPrice,
          setMargin: this.setWeightMarginPrice,
          sortable: true,
        },
        {
          header: "Cost (no margin)",
          field: "noMarginPrice",
          type: tableColumn.number,
        },
      ];
      columns.push({
        header: "Departure",
        field: "departureDate",
        type: tableColumn.date,
      });
      columns.push({
        header: "Delivery",
        field: "deliveryDate",
        type: tableColumn.date,
      });
    }

    if (
      quoteDiscriminator == quoteType.ftl ||
      quoteDiscriminator == quoteType.ptl
    ) {
      columns = [
        ...columns,
        {
          header: "Price (Shipment)",
          type: tableColumn.priceBox,
          field: "blPrice",
          getNet: this.getShipmentNetPrice,
          setNet: this.setShipmentNetPrice,
          getMargin: this.getShipmentMarginPrice,
          setMargin: this.setShipmentMarginPrice,
          sortable: true,
        },
      ];
    }

    columns.push({
      header: "Currency",
      field: "currency",
      type: tableColumn.currency,
    });
    if (quoteDiscriminator != quoteType.ltl) {
      columns.push({
        header: "",
        type: tableColumn.link,
        field: "externalLink",
      });
    }
    columns.push({
      header: "",
      field: "comment",
      type: tableColumn.comment,
    });
    columns.push({ header: "", field: "info", type: tableColumn.info });
    if (quoteDiscriminator == quoteType.ltl) {
      columns.push(
        { header: "", field: "debugInfo", type: tableColumn.debug },
        {
          header: "Validity",
          field: "validity",
          type: tableColumn.datePicker,
          invalid: (freightRate: FreightRate) =>
            freightRate.isExpiredSpotRate(),
          warning: (freightRate: FreightRate) =>
            freightRate.validityWarningMessage,
        },
      );
    }

    return columns;
  }
}
